import withIntersectionObserver from '@studiometa/js-toolkit/decorators/withIntersectionObserver';
import gsap from 'gsap';
import random from 'canvas-sketch-util/random';
import Canvas from './Canvas.js';
import getSize from '../utils/getSize.js';

/**
 * CanvasMenu class
 */
export default class CanvasHome extends withIntersectionObserver(Canvas) {
  /**
   * Config
   */
  get config() {
    return {
      name: 'CanvasHome',
    };
  }

  /**
   * Triggered when the root element of the component is intersecting.
   * @param  {Array<IntersectionObserverEntry>} entries
   */
  intersected(entries) {
    if (entries[0].isIntersecting && !this.isVisible) {
      this.changeSlideAnimation();
      this.isVisible = true;
    }
  }

  /**
   * Init
   */
  init() {
    // Canvas cover size
    if (this.$options.size === 'cover') {
      const { width, height } = this.$el.getBoundingClientRect();
      this.width = width;
      this.height = height;
      return super.init();
    }

    // Canvas given size
    this.width = this.$options.width;
    this.height = this.$options.height;

    return super.init();
  }

  /**
   * Resized hook
   */
  resized() {
    // Resize the PIXI renderer (it will automaticcaly fit screen because of the resizeTo param used in createPixiApp)
    if (this.app && this.$options.size === 'cover') {
      const { width, height } = this.$el.getBoundingClientRect();
      this.width = width;
      this.height = height;
      this.app.renderer.resize(width, height);
    }

    // Redraw all sprites to fit new screen size
    if (this.sprites.length > 0) {
      this.sprites.forEach((sprite) => {
        this.setSpriteSize(sprite);
      });
    }
  }

  /**
   * Reset slides
   */
  setSpriteSize(sprite) {
    if (this.$options.size === 'cover') {
      // Get a size for the slide that mimic background-size: cover
      const slideSize = getSize('cover', sprite.width, sprite.height, this.width, this.height);
      sprite.width = slideSize.width;
      sprite.height = slideSize.height;
    }

    // Center the slide
    sprite.anchor.set(0.5);
    sprite.position.x = this.width / 2;
    sprite.position.y = this.height / 2;
  }

  /**
   * Get Images URL from HTML attribute
   */
  getImages() {
    this.images = {};

    this.$refs.images.forEach((item, i) => {
      const image = item.dataset.img;
      this.images[i] = { image };
    });

    return this.images;
  }

  /**
   * No after load animation
   */
  afterLoadAnimation() {
    // Silence.
  }

  /**
   * Change slide animation
   */
  changeSlideAnimation() {
    if (this.displacementFilter && this.displacementSprite) {
      // Get Random Values for the displacementSprite
      // in order to have unique animations at each change
      const randomX = random.range(-200, 200);
      const randomY = random.range(-200, 200);

      // Set distorsion
      gsap.set(this.displacementFilter.scale, { x: 150, y: 150 });
      gsap.set(this.displacementSprite, { x: randomX, y: randomY });

      // Fade in the sprite image
      gsap.to(this.sprites[0], { alpha: 1.3, ease: 'sine.out', delay: 0.2 });

      // Create a Timeline for the animation
      const tl = gsap.timeline();

      // Reset the distortions
      tl.to(this.displacementFilter.scale, { x: 0, y: 0, duration: 2.5, ease: 'power3.out' }, 0.4);
      tl.to(this.displacementSprite, { x: 0, y: 0, duration: 2.5, ease: 'power3.in' }, 0.4);
    }
  }
}
